export type Direction = 'left' | 'right' | 'up' | 'down';

export interface GameState {
    board: Array<number>;
    score: number;
}

interface TileMovement {
    from: number;
    to: number;
}

export const boardSize = 4;
export const winningTile = 2048;
export const cellPositions = Array.from({length: boardSize * boardSize}, (_, index) => index);
const initialTile = 2;
const commonTileProbability = 0.9;

const getLinePositions = (direction: Direction, line: number): Array<number> =>
    Array.from({length: boardSize}, (_, offset) => {
        const position = direction === 'right' || direction === 'down' ? boardSize - 1 - offset : offset;
        const horizontal = direction === 'left' || direction === 'right';

        return horizontal ? line * boardSize + position : position * boardSize + line;
    });

const getLineMovements = (board: Array<number>, positions: Array<number>): Array<TileMovement> => {
    const occupied = positions.filter(position => board[position] !== 0);
    const movements: Array<TileMovement> = [];
    let destination = 0;
    for (let index = 0; index < occupied.length; index++) {
        const from = occupied[index] ?? 0;
        const next = occupied[index + 1];
        const to = positions[destination] ?? 0;
        movements.push({from, to});
        if (next !== undefined && board[from] === board[next]) {
            movements.push({from: next, to});
            index++;
        }
        destination++;
    }

    return movements;
};

/** Keep the source and destination of both tiles when a pair merges. */
export const getTileMovements = (board: Array<number>, direction: Direction): Array<TileMovement> =>
    Array.from({length: boardSize}, (_, line) => getLineMovements(board, getLinePositions(direction, line))).flat();

/** Slide and merge once per tile. An ineffective move preserves the original state. */
export const slideTiles = (state: GameState, direction: Direction): GameState => {
    const board = cellPositions.map(() => 0);
    let {score} = state;
    for (const {from, to} of getTileMovements(state.board, direction)) {
        const existing = board[to] ?? 0;
        const value = state.board[from] ?? 0;
        board[to] = existing + value;
        if (existing) score += existing + value;
    }

    return board.some((tile, index) => tile !== state.board[index]) ? {board, score} : state;
};

/** Add a 2 (90%) or 4 (10%) to a random empty cell, without mutating the board. */
export const addRandomTile = (
    board: Array<number>,
    randomPosition = Math.random(),
    randomValue = Math.random(),
): Array<number> => {
    const empty = cellPositions.filter(position => board[position] === 0);
    const position = empty[Math.floor(randomPosition * empty.length)];
    if (position === undefined) return board;
    const next = [...board];
    next[position] = randomValue < commonTileProbability ? initialTile : initialTile * initialTile;

    return next;
};

/** Start with two tiles and a fresh score. */
export const createGame = (): GameState => ({
    board: addRandomTile(addRandomTile(cellPositions.map(() => 0))),
    score: 0,
});

/** Spawn a new tile only after the player actually moves or merges tiles. */
export const moveGame = (
    state: GameState,
    direction: Direction,
    randomPosition = Math.random(),
    randomValue = Math.random(),
): GameState => {
    const next = slideTiles(state, direction);

    return next === state ? state : {...next, board: addRandomTile(next.board, randomPosition, randomValue)};
};

/** An empty cell or two equal orthogonal neighbours allow another move. */
export const hasMoves = (board: Array<number>): boolean => board.some((tile, index) =>
    tile === 0
    || (index % boardSize < boardSize - 1 && tile === board[index + 1])
    || (index < boardSize * (boardSize - 1) && tile === board[index + boardSize]));
